import { useCommonTranslation } from 'c-translation';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { Box, FormLabel, Stack, Typography } from '@mui/material';
import { Alert, AllEntitiesDropdown, AutoGrid, Button, SelectField, TextField } from 'c-components';
import { NetworkRequestState } from 'c-data-layer';
import { Redirect } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';
import { TimePicker } from '@mui/x-date-pickers-pro';

export const DisplayOwnerForm = ({ control, state, error, id, watch }) => {
    const t = useCommonTranslation();
    const popRefreshType = watch('pop_refresh_type');
    const popRefreshOffset = watch('pop_refresh_offset.logs.minutes');
    const logImportSource = watch('logImportSource');

    const lastPlayedAt = useMemo(() => {
        if (!logImportSource || !Array.isArray(logImportSource)) {
            return null;
        }

        const validDates = logImportSource
            .map(source => source.last_played_at)
            .filter(date => !!date)
            .map(date => new Date(date));

        if (validDates.length === 0) {
            return null;
        }

        return new Date(Math.max(...validDates.map(d => d.getTime())));
    }, [logImportSource]);

    const humanReadable = useMemo(() => {
        const rawMins = popRefreshOffset;
        const isNegative = rawMins < 0;
        const mins = Math.abs(rawMins);
        const hrs = Math.floor(mins / 60);
        const rem = mins % 60;

        const label = `${hrs > 0 ? `${hrs} hour${hrs > 1 ? 's' : ''} ` : ''}${rem} min${
            rem !== 1 ? 's' : ''
        }`;
        return isNegative ? `-${label.trim()}` : label;
    }, [popRefreshOffset]);

    const SuccessAlert = useMemo(() => {
        if (state === 'success') {
            return (
                <>
                    <Alert severity="success">
                        {t('Modules.Displays.DisplaysOwnerEdit.success')}
                    </Alert>
                    <Redirect to={PostAuthRoutes.Displays.OwnerList} />
                </>
            );
        }
        return null;
    }, [state, t]);

    const SubmitButton = useMemo(
        () => (
            <Button type="submit" disabled={state === NetworkRequestState.InProgress}>
                {t('Modules.Displays.DisplaysOwnerEdit.submit')}
            </Button>
        ),
        [state, t],
    );

    return (
        <Box>
            <AutoGrid xs={5} gap={2} pb={2}>
                <Controller
                    name="countries"
                    control={control}
                    render={({ field }) => (
                        <>
                            <FormLabel sx={{ color: 'red' }}>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.country')} *
                            </FormLabel>
                            <AllEntitiesDropdown
                                entityName="Displays_Country"
                                useLocalEntityData
                                labelField="name"
                                multi
                                {...field}
                            />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="name"
                    render={({ field }) => (
                        <>
                            <FormLabel sx={{ color: 'red' }}>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.name')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="partner_id"
                    render={({ field }) => (
                        <>
                            <FormLabel sx={{ color: 'red' }}>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.partner')} *
                            </FormLabel>
                            <AllEntitiesDropdown
                                entityName="Displays_Partner"
                                labelField="name"
                                {...field}
                                useLocalEntityData
                            />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="markets"
                    render={({ field }) => (
                        <>
                            <FormLabel sx={{ color: 'red' }}>
                                {t('Modules.Displays.DisplaysDisplayEdit.fields.market')} *
                            </FormLabel>
                            <AllEntitiesDropdown
                                entityName="Market"
                                labelField="name"
                                multi
                                useLocalEntityData
                                {...field}
                            />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="address_line_1"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.address1')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="address_line_2"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.address2')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="address_line_3"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.address3')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="address_line_4"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.address4')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="postcode"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.postcode')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="external_id"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.extId')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    name="vendor_name"
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.vendor')}
                            </FormLabel>
                            <TextField {...field} />
                        </>
                    )}
                />
                <Controller
                    control={control}
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysDisplayEdit.fields.status')}
                            </FormLabel>
                            <SelectField
                                value={field.value}
                                options={[
                                    {
                                        value: 0,
                                        label: t(
                                            'Modules.Displays.DisplaysOwnerEdit.fields.disable',
                                        ),
                                    },
                                    {
                                        value: 1,
                                        label: t(
                                            'Modules.Displays.DisplaysOwnerEdit.fields.enable',
                                        ),
                                    },
                                ]}
                                {...field}
                            />
                        </>
                    )}
                    name="status"
                />
                <Controller
                    render={({ field }) => (
                        <>
                            <FormLabel>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.logSource')}
                            </FormLabel>
                            <AllEntitiesDropdown
                                entityName="PopDisplays_LogImportSource"
                                labelField="name"
                                multi
                                useLocalEntityData
                                {...field}
                            />
                        </>
                    )}
                    control={control}
                    name="log_import_sources"
                />
                <Stack>
                    <FormLabel>
                        {t('Modules.Displays.DisplaysOwnerEdit.fields.lastPlayedAt')}:
                    </FormLabel>
                    <Typography variant="body2" color="textSecondary">
                        <TextField value={lastPlayedAt || 'N/A'} disabled />
                    </Typography>
                </Stack>
                <Controller
                    control={control}
                    name="pop_refresh_type"
                    render={({ field }) => (
                        <Box>
                            <FormLabel sx={{ mb: 1 }}>
                                {t('Modules.Displays.DisplaysOwnerEdit.fields.popRefreshType')}
                            </FormLabel>
                            <SelectField
                                {...field}
                                sx={{ minWidth: '200px' }}
                                options={[
                                    {
                                        value: 'days',
                                        label: t('Modules.Displays.DisplaysOwnerEdit.fields.days'),
                                    },
                                    {
                                        value: 'logs',
                                        label: t('Modules.Displays.DisplaysOwnerEdit.fields.logs'),
                                    },
                                ]}
                            />
                        </Box>
                    )}
                />

                {popRefreshType === 'days' && (
                    <AutoGrid xs={3} gap={2}>
                        <Controller
                            name="pop_refresh_offset.days.days"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <FormLabel>
                                        {t('Modules.Displays.DisplaysOwnerEdit.fields.days')}
                                    </FormLabel>
                                    <TextField type="number" {...field} />
                                </Box>
                            )}
                        />
                        <Controller
                            name="pop_refresh_offset.days.time"
                            control={control}
                            render={({ field }) => {
                                // Convert "HH:mm" string to Date
                                const parseTimeStringToDate = (str: string) => {
                                    if (!str) return null;
                                    const [hours, minutes] = str.split(':').map(Number);
                                    const date = new Date();
                                    date.setHours(hours);
                                    date.setMinutes(minutes);
                                    date.setSeconds(0);
                                    date.setMilliseconds(0);
                                    return date;
                                };
                                const formatDateToTimeString = (date: Date) => {
                                    const hours = String(date.getHours()).padStart(2, '0');
                                    const minutes = String(date.getMinutes()).padStart(2, '0');
                                    return `${hours}:${minutes}`;
                                };

                                return (
                                    <Box>
                                        <FormLabel>
                                            {t('Modules.Displays.DisplaysOwnerEdit.fields.time')}
                                        </FormLabel>
                                        <TimePicker
                                            ampm={false}
                                            value={parseTimeStringToDate(field.value)}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    field.onChange(formatDateToTimeString(date));
                                                } else {
                                                    field.onChange('');
                                                }
                                            }}
                                            slotProps={{
                                                textField: {
                                                    size: 'medium',
                                                    variant: 'outlined',
                                                    sx: {
                                                        backgroundColor: 'white',
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                );
                            }}
                        />
                    </AutoGrid>
                )}
                {popRefreshType === 'logs' && (
                    <AutoGrid xs={3} gap={2} alignItems="center">
                        <Controller
                            name="pop_refresh_offset.logs.minutes"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <FormLabel>
                                        {t('Modules.Displays.DisplaysOwnerEdit.fields.mins')}
                                    </FormLabel>
                                    <TextField
                                        type="number"
                                        {...field}
                                        onChange={e => {
                                            const value = parseInt(e.target.value, 10);
                                            // eslint-disable-next-line no-restricted-globals
                                            field.onChange(isNaN(value) ? '' : Math.min(value, 0));
                                        }}
                                    />
                                </Box>
                            )}
                        />
                        {humanReadable && (
                            <Box pt={3}>
                                <Typography variant="body2" color="textSecondary">
                                    {humanReadable}
                                </Typography>
                            </Box>
                        )}
                    </AutoGrid>
                )}
            </AutoGrid>
            {state === NetworkRequestState.Error && (
                <Box pb={2}>
                    <Alert severity="error" variant="outlined">
                        {String(error)}
                    </Alert>
                </Box>
            )}
            {SubmitButton}
            {SuccessAlert}
        </Box>
    );
};
